import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowRight, BarChart3, Users, Target, MessageSquare, TrendingUp, DollarSign, Shield, Sparkles } from 'lucide-react';

const modelIcons = {
  1: BarChart3,
  2: Users,
  3: Target,
  4: MessageSquare,
  5: TrendingUp,
  6: DollarSign,
  7: Shield,
};

const vibrantColors = [
  'from-orange-400 to-pink-500',
  'from-blue-400 to-purple-500',
  'from-green-400 to-blue-500',
  'from-purple-400 to-pink-500',
  'from-yellow-400 to-orange-500',
  'from-pink-400 to-red-500',
  'from-indigo-400 to-purple-500',
];

const BentoGrid = ({ cards }) => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9,
      rotateX: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6
      }
    },
  };

  const hoverVariants = {
    hover: {
      y: -12,
      scale: 1.03,
      rotateY: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25,
      }
    }
  };

  const iconVariants = {
    hover: {
      scale: 1.2,
      rotate: 360,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      }
    }
  };

  const gradientVariants = {
    hover: {
      backgroundPosition: "200% center",
      transition: {
        duration: 0.8,
        ease: "easeInOut"
      }
    }
  };

  // Define grid layout patterns for different card positions
  const getGridClass = (index) => {
    const patterns = [
      "md:col-span-2 md:row-span-2", // Wide card
      "md:col-span-1 md:row-span-1", // Tall card
      "md:col-span-1 md:row-span-1", // Regular card
      "md:col-span-2 md:row-span-1", // Wide card
      "md:col-span-1 md:row-span-1", // Regular card
      "md:col-span-1 md:row-span-1", // Regular card
      "md:col-span-2 md:row-span-1", // Wide card
    ];
    // return patterns[index % patterns.length];
    return patterns[1];
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4] dark:bg-black dark:text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-chart-1/20 to-chart-2/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-accent/10 to-primary/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Hero Section */}
      <motion.section
        className="relative overflow-hidden py-24 px-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            {/* <motion.div
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-accent/10 to-primary/10 backdrop-blur-sm border border-accent/20 rounded-full px-6 py-3 mb-8"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <Sparkles className="w-5 h-5 text-accent" />
              <span className="text-sm font-medium text-accent">Powered by Express Analytics</span>
            </motion.div> */}

            <h1 className="text-6xl md:text-8xl font-bold tracking-tight mb-8">
              <motion.span
                className="block"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Express Analytics
              </motion.span>
              <motion.span
                className="block bg-gradient-to-r from-accent via-primary to-chart-1 bg-clip-text "
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Models
              </motion.span>
            </h1>

            <motion.p
              className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Explore our comprehensive suite of advanced analytics and machine learning models designed to unlock insights from your data with stunning visualizations and interactive experiences.
            </motion.p>
          </motion.div>
        </div>
      </motion.section>

      {/* Bento Grid Section */}
      <motion.section
        className="px-6 pb-24 relative"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
            {cards.map((card, index) => {
              const IconComponent = modelIcons[card.id] || BarChart3;
              const gradientClass = vibrantColors[index % vibrantColors.length];

              return (
                <motion.div
                  key={card.id}
                  variants={itemVariants}
                  whileHover="hover"
                  className={`${getGridClass(index)} group perspective-1000`}

                >
                  <motion.div
                    variants={hoverVariants}
                    className="h-full"
                  >
                    <Card
                      className="h-full cursor-pointer border-0 bg-card/60 backdrop-blur-xl hover:bg-card/80 transition-all duration-700 relative overflow-hidden group vibrant-hover"
                      onClick={() => navigate(`/model/${card.id}/${encodeURIComponent(card.title)}`)}
                      style={{
                        background: `linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--card)/0.8) 100%)`,
                        boxShadow: `0 8px 32px hsl(var(--accent)/0.1)`,
                      }}
                    >
                      {/* Gradient overlay on hover */}
                      <motion.div
                        className={`absolute inset-0 bg-gradient-to-br ${gradientClass} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                        variants={gradientVariants}
                      />

                      {/* Shimmer effect */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                      </div>

                      <CardHeader className="pb-4 relative z-10 mt-8">
                        <div className="flex items-start">
                          <motion.div
                            className={`p-4 rounded-2xl bg-gradient-to-br ${gradientClass} text-white shadow-lg group-hover:shadow-xl transition-all duration-300`}
                            variants={iconVariants}
                            whileHover="hover"
                          >
                            <IconComponent className="w-7 h-7" />

                          </motion.div>
                          <motion.div
                            className='ml-5'
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                          >
                            <CardTitle className="text-lg group-hover:text-accent transition-colors duration-300  ">
                              {card.title}
                            </CardTitle>
                          </motion.div>



                        </div>

                        {/* <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 * index }}
                        >
                          <CardTitle className="text-xl group-hover:text-accent transition-colors duration-300 mt-4">
                            {card.title}
                          </CardTitle>
                        </motion.div> */}

                      </CardHeader>

                      <CardContent className="relative z-10 mt-5">
                        {/* <CardDescription className="text-justify leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                          {card.description}
                        </CardDescription> */}

                        <img
                          src={card.image} // Replace with your image field or path
                          alt="Card visual"
                          className="w-full h-auto mt-4 rounded-lg object-cover"
                        />

                        {/* Progress indicator */}
                        <motion.div
                          className="mt-6 h-1 bg-muted rounded-full overflow-hidden"
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ delay: 0.2 * index, duration: 0.8 }}
                        >
                          <motion.div
                            className={`h-full bg-gradient-to-r ${gradientClass} rounded-full`}
                            initial={{ x: "-100%" }}
                            animate={{ x: "0%" }}
                            transition={{ delay: 0.3 * index, duration: 1 }}
                          />
                        </motion.div>

                        <div className="flex justify-end">
                          <motion.div
                            className="opacity-0 group-hover:opacity-100 transition-all duration-300 mt-5"
                            whileHover={{ x: 6, scale: 1.1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                          >
                            <div className="p-2 rounded-full bg-accent/10 backdrop-blur-sm">
                              <ArrowRight className="w-5 h-5 text-accent" />
                            </div>
                          </motion.div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Call to action section */}
      <motion.section
        className="px-6 pb-24"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            className="bg-gradient-to-r from-accent/10 to-primary/10 backdrop-blur-xl border border-accent/20 rounded-3xl p-12"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6  bg-gradient-to-r from-accent to-primary bg-clip-text">
              Ready to Transform Your Data?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover the power of advanced analytics and machine learning with our comprehensive suite of data science models.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-accent to-primary hover:from-accent/90 hover:to-primary/90 text-primary shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => { window.open("https://oyster.expressanalytics.net/", "_blank") }}
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default BentoGrid;